import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Channel {
  id: string;
  name: string;
  channel_type: 'department' | 'entity' | 'custom';
  department_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  created_by: string;
  created_at: string;
  department?: {
    id: string;
    name: string;
    slug: string;
    color: string | null;
    icon: string | null;
  };
  unreadCount?: number;
  lastMessage?: string;
  lastMessageAt?: string;
}

export interface ChannelMessage {
  id: string;
  channel_id: string;
  sender_id: string;
  message: string;
  attachments: any[];
  created_at: string;
  parent_id: string | null;
  reply_count: number;
  sender?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
}

export interface EntityThread {
  entity_type: string;
  entity_id: string;
  entity_name: string;
  unreadCount: number;
  lastMessage: string;
  lastMessageAt: string;
}

export interface MentionProfile {
  id: string;
  full_name: string | null;
  email: string | null;
}

export const useMessageChannels = (channelId?: string) => {
  const queryClient = useQueryClient();

  // Fetch all profiles for @mentions
  const { data: profiles = [] } = useQuery({
    queryKey: ["all-profiles-for-mentions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .order("full_name");
      if (error) throw error;
      return data as MentionProfile[];
    },
  });

  // Fetch all channels (department + entity + custom)
  const { data: channels = [], isLoading: isLoadingChannels } = useQuery({
    queryKey: ["message-channels"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");
      const userId = session.session.user.id;

      // Fetch department info
      const { data: departments, error: deptError } = await supabase
        .from("departments")
        .select("id, name, slug, color, icon")
        .eq("is_active", true)
        .order("sort_order");

      if (deptError) throw deptError;

      // Fetch existing department channels, sorted oldest-first so the original wins on any future duplicate
      const { data: existingDeptChannels } = await supabase
        .from("message_channels")
        .select("*")
        .eq("channel_type", "department")
        .order("created_at", { ascending: true });

      // Earliest-wins: only set the map entry if not already present
      const existingDeptMap = new Map<string, any>();
      for (const ch of existingDeptChannels || []) {
        if (ch.department_id && !existingDeptMap.has(ch.department_id)) {
          existingDeptMap.set(ch.department_id, ch);
        }
      }

      // Ensure each department has a real channel record
      const deptChannels: Channel[] = [];
      
      for (const dept of departments || []) {
        let channelRecord = existingDeptMap.get(dept.id);
        
        // Create channel if it doesn't exist (race-safe: handle unique violation by re-fetching)
        if (!channelRecord) {
          const { data: newChannel, error: createError } = await supabase
            .from("message_channels")
            .insert({
              name: `#${dept.slug}`,
              channel_type: "department",
              department_id: dept.id,
              created_by: userId,
            })
            .select()
            .single();

          if (createError) {
            // 23505 = unique violation: another tab/user just created it. Re-fetch the existing one.
            if ((createError as any).code === "23505") {
              const { data: existing } = await supabase
                .from("message_channels")
                .select("*")
                .eq("channel_type", "department")
                .eq("department_id", dept.id)
                .order("created_at", { ascending: true })
                .limit(1)
                .maybeSingle();
              if (existing) {
                channelRecord = existing;
              } else {
                console.error("Error creating department channel:", createError);
                continue;
              }
            } else {
              console.error("Error creating department channel:", createError);
              continue;
            }
          } else {
            channelRecord = newChannel;
          }
        }

        deptChannels.push({
          id: channelRecord.id, // Use the REAL UUID from message_channels
          name: channelRecord.name,
          channel_type: 'department',
          department_id: dept.id,
          entity_type: null,
          entity_id: null,
          created_by: channelRecord.created_by,
          created_at: channelRecord.created_at,
          department: dept,
          unreadCount: 0,
        });
      }

      // Get last read times
      const channelIds = deptChannels.map(c => c.id);
      const { data: readStatus } = await supabase
        .from("channel_message_reads")
        .select("channel_id, last_read_at")
        .eq("user_id", userId)
        .in("channel_id", channelIds);

      const readMap = new Map(readStatus?.map(r => [r.channel_id, r.last_read_at]) || []);

      // Get last messages and unread counts for each channel
      for (const channel of deptChannels) {
        const { data: messages } = await supabase
          .from("channel_messages")
          .select("message, created_at, sender_id")
          .eq("channel_id", channel.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (messages && messages.length > 0) {
          channel.lastMessage = messages[0].message;
          channel.lastMessageAt = messages[0].created_at;

          const lastRead = readMap.get(channel.id);
          if (lastRead) {
            const { count } = await supabase
              .from("channel_messages")
              .select("*", { count: "exact", head: true })
              .eq("channel_id", channel.id)
              .neq("sender_id", userId)
              .gt("created_at", lastRead);
            channel.unreadCount = count || 0;
          } else {
            const { count } = await supabase
              .from("channel_messages")
              .select("*", { count: "exact", head: true })
              .eq("channel_id", channel.id)
              .neq("sender_id", userId);
            channel.unreadCount = count || 0;
          }
        }
      }

      return deptChannels;
    },
  });

  // Fetch custom (user-created) channels
  const { data: customChannels = [], isLoading: isLoadingCustomChannels } = useQuery({
    queryKey: ["custom-channels"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");
      const userId = session.session.user.id;

      // Fetch custom channels where user is a member
      const { data: memberChannels, error: memberError } = await supabase
        .from("channel_members")
        .select("channel_id")
        .eq("user_id", userId);

      if (memberError) throw memberError;

      const memberChannelIds = memberChannels?.map(m => m.channel_id) || [];
      if (memberChannelIds.length === 0) return [];

      // Fetch the custom channels the user is a member of
      const { data: customChannelData, error } = await supabase
        .from("message_channels")
        .select("*")
        .eq("channel_type", "custom")
        .in("id", memberChannelIds);

      if (error) throw error;

      const customChs: Channel[] = [];

      for (const channel of customChannelData || []) {
        // Get last read time
        const { data: readStatus } = await supabase
          .from("channel_message_reads")
          .select("last_read_at")
          .eq("channel_id", channel.id)
          .eq("user_id", userId)
          .single();

        // Get last message
        const { data: messages } = await supabase
          .from("channel_messages")
          .select("message, created_at")
          .eq("channel_id", channel.id)
          .order("created_at", { ascending: false })
          .limit(1);

        let unreadCount = 0;
        if (messages && messages.length > 0) {
          if (readStatus?.last_read_at) {
            const { count } = await supabase
              .from("channel_messages")
              .select("*", { count: "exact", head: true })
              .eq("channel_id", channel.id)
              .neq("sender_id", userId)
              .gt("created_at", readStatus.last_read_at);
            unreadCount = count || 0;
          } else {
            const { count } = await supabase
              .from("channel_messages")
              .select("*", { count: "exact", head: true })
              .eq("channel_id", channel.id)
              .neq("sender_id", userId);
            unreadCount = count || 0;
          }
        }

        customChs.push({
          id: channel.id,
          name: channel.name,
          channel_type: 'custom',
          department_id: null,
          entity_type: null,
          entity_id: null,
          created_by: channel.created_by,
          created_at: channel.created_at,
          unreadCount,
          lastMessage: messages?.[0]?.message,
          lastMessageAt: messages?.[0]?.created_at,
        });
      }

      return customChs;
    },
  });

  // Fetch messages for a specific channel
  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: ["channel-messages", channelId],
    queryFn: async () => {
      if (!channelId) return [];

      const { data, error } = await supabase
        .from("channel_messages")
        .select("id, channel_id, sender_id, message, attachments, created_at, parent_id, reply_count")
        .eq("channel_id", channelId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch sender profiles
      const senderIds = new Set<string>();
      data?.forEach(msg => senderIds.add(msg.sender_id));

      const { data: senderProfiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", Array.from(senderIds));

      const profileMap = new Map(senderProfiles?.map(p => [p.id, p]) || []);

      return data?.map(msg => ({
        ...msg,
        sender: profileMap.get(msg.sender_id),
      })) as ChannelMessage[];
    },
    enabled: !!channelId,
  });

  // Fetch entity threads (leads, bookings with messages)
  const { data: entityThreads = [], isLoading: isLoadingEntityThreads } = useQuery({
    queryKey: ["entity-threads"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      // Get all entity channels
      const { data: entityChannels, error } = await supabase
        .from("message_channels")
        .select("*")
        .eq("channel_type", "entity");

      if (error) throw error;
      if (!entityChannels || entityChannels.length === 0) return [];

      // Enrich with entity names and message info
      const threads: EntityThread[] = [];
      
      for (const channel of entityChannels) {
        let entityName = "Unknown";
        
        // Fetch entity name based on type
        if (channel.entity_type === "lead" && channel.entity_id) {
          const { data: lead } = await supabase
            .from("leads")
            .select("title")
            .eq("id", channel.entity_id)
            .single();
          entityName = lead?.title || "Unknown Lead";
        } else if (channel.entity_type === "booking" && channel.entity_id) {
          const { data: booking } = await supabase
            .from("coffeebeans_bookings")
            .select("booking_number, guest_name")
            .eq("id", channel.entity_id)
            .single();
          entityName = booking ? `${booking.booking_number} - ${booking.guest_name}` : "Unknown Booking";
        }

        // Get last message
        const { data: lastMsg } = await supabase
          .from("channel_messages")
          .select("message, created_at")
          .eq("channel_id", channel.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (lastMsg && lastMsg.length > 0) {
          threads.push({
            entity_type: channel.entity_type || "",
            entity_id: channel.entity_id || "",
            entity_name: entityName,
            unreadCount: 0,
            lastMessage: lastMsg[0].message,
            lastMessageAt: lastMsg[0].created_at,
          });
        }
      }

      return threads;
    },
  });

  // Send message to a channel
  const { mutateAsync: sendChannelMessage, isPending: isSending } = useMutation({
    mutationFn: async ({ 
      channelId, 
      message, 
      mentionedUserIds = [],
      attachments,
    }: { 
      channelId: string; 
      message: string; 
      mentionedUserIds?: string[];
      attachments?: Array<{ id: string; name: string; url: string; type: string; size: number }>;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      const userId = session.session.user.id;

      const { data, error } = await supabase
        .from("channel_messages")
        .insert({
          channel_id: channelId,
          sender_id: userId,
          message,
          attachments: attachments || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Create notifications for mentioned users
      if (mentionedUserIds.length > 0) {
        // Get sender name for notification
        const { data: senderProfile } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", userId)
          .single();

        const senderName = senderProfile?.full_name || senderProfile?.email || "Someone";

        const notifications = mentionedUserIds
          .filter(uid => uid !== userId) // Don't notify yourself
          .map(mentionedUserId => ({
            user_id: mentionedUserId,
            title: `${senderName} mentioned you in a thread`,
            message: message.slice(0, 100) + (message.length > 100 ? "..." : ""),
            type: "mention",
            related_entity_id: channelId,
            related_entity_type: "channel_message",
          }));

        if (notifications.length > 0) {
          await supabase.from("dashboard_notifications").insert(notifications);
        }
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // Auto-mark channel as read after sending so own message doesn't count as unread
      markChannelAsRead(variables.channelId);
      queryClient.invalidateQueries({ queryKey: ["channel-messages", channelId] });
      queryClient.invalidateQueries({ queryKey: ["message-channels"] });
      queryClient.invalidateQueries({ queryKey: ["custom-channels"] });
    },
  });

  // Mark channel as read
  const { mutateAsync: markChannelAsRead } = useMutation({
    mutationFn: async (targetChannelId: string) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("channel_message_reads")
        .upsert({
          channel_id: targetChannelId,
          user_id: session.session.user.id,
          last_read_at: new Date().toISOString(),
        }, {
          onConflict: "channel_id,user_id",
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-channels"] });
    },
  });

  // Get or create entity channel
  const { mutateAsync: getOrCreateEntityChannel } = useMutation({
    mutationFn: async ({ entityType, entityId, entityName }: { 
      entityType: string; 
      entityId: string; 
      entityName: string;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      // Check if channel already exists (earliest wins for safety)
      const { data: existing } = await supabase
        .from("message_channels")
        .select("*")
        .eq("channel_type", "entity")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (existing) return existing;

      // Create new channel (race-safe: handle unique violation by re-fetching the winner)
      const { data, error } = await supabase
        .from("message_channels")
        .insert({
          name: entityName,
          channel_type: "entity",
          entity_type: entityType,
          entity_id: entityId,
          created_by: session.session.user.id,
        })
        .select()
        .single();

      if (error) {
        if ((error as any).code === "23505") {
          const { data: raceWinner } = await supabase
            .from("message_channels")
            .select("*")
            .eq("channel_type", "entity")
            .eq("entity_type", entityType)
            .eq("entity_id", entityId)
            .order("created_at", { ascending: true })
            .limit(1)
            .maybeSingle();
          if (raceWinner) return raceWinner;
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entity-threads"] });
    },
  });

  // Combine department and custom channels
  const allChannels = [...channels, ...customChannels];

  // Calculate total unread across all channels
  const totalUnread = allChannels.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return {
    channels: allChannels,
    messages,
    entityThreads,
    profiles,
    isLoadingChannels: isLoadingChannels || isLoadingCustomChannels,
    isLoadingMessages,
    isLoadingEntityThreads,
    sendChannelMessage,
    isSending,
    markChannelAsRead,
    getOrCreateEntityChannel,
    totalUnread,
  };
};
